import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import {
  Body, Button, Checkbox, I, Icon, Label, Dialog, Row, Stack, } from '@leanin/ui';
import type { PostMediaView } from '@leanin/shared';

import { PostMediaDocumentCard } from './PostMediaDocumentCard';

const TILE_RADIUS = 'var(--radius-md)';
const MAX_VISIBLE = 4;

type GalleryProps = {
  items: ReadonlyArray<PostMediaView>;
};

type Layout = '1' | '2' | '3' | '4';

function layoutFor(count: number): Layout {
  if (count <= 1) return '1';
  if (count === 2) return '2';
  if (count === 3) return '3';
  return '4';
}

function gridStyleFor(layout: Layout): CSSProperties {
  switch (layout) {
    case '1':
      return { display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-xs)' };
    case '2':
      return {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-xs)',
      };
    case '3':
      return {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 'var(--space-xs)',
      };
    case '4':
      return {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 'var(--space-xs)',
      };
  }
}

function tileSpanFor(layout: Layout, index: number): CSSProperties {
  if (layout === '3' && index === 0) {
    return { gridRow: 'span 2' };
  }
  return {};
}

// Shared wrapper styles for the image-tile aspect-ratio container — both the
// read-only `<button>` tile and the editable `<div>` tile derive from this
// so the composer preview matches the rendered post tile pixel-for-pixel.
function imageTileWrapperStyles(extra?: CSSProperties): CSSProperties {
  return {
    display: 'block',
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    padding: 0,
    border: 'none',
    borderRadius: TILE_RADIUS,
    background: 'var(--muted)',
    overflow: 'hidden',
    ...extra,
  };
}

// A single image renders uncropped at its natural aspect ratio, capped to a
// max height so a tall portrait image doesn't make the post enormous. Width
// shrinks proportionally for tall images (left-aligned, no letterbox bars, no
// crop); wide images fill the column. Multi-image collages keep the cropped
// 4:3 grid tiles below — un-cropping a 2×2 grid would break the mosaic.
const SINGLE_IMAGE_MAX_HEIGHT = '32rem';

function singleImageStyles(): CSSProperties {
  return {
    display: 'block',
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: SINGLE_IMAGE_MAX_HEIGHT,
    margin: 0,
    borderRadius: TILE_RADIUS,
  };
}

function PostMediaImageBody({ item }: { item: PostMediaView }) {
  return (
    <img
      src={item.url}
      alt={item.decorative ? '' : (item.altText ?? '')}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
      {...(item.decorative ? { role: 'presentation' as const } : {})}
    />
  );
}

function PostMediaSingleImage({
  item,
  onOpen,
}: {
  item: PostMediaView;
  onOpen: () => void;
}) {
  const t = useTranslations('Feed');
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={item.altText ?? t('media.openImage')}
      style={{
        display: 'block',
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'zoom-in',
      }}
    >
      <img
        src={item.url}
        alt={item.decorative ? '' : (item.altText ?? '')}
        style={singleImageStyles()}
        {...(item.decorative ? { role: 'presentation' as const } : {})}
      />
    </button>
  );
}

export function PostMediaGallery({ items }: GalleryProps) {
  const t = useTranslations('Feed');
  const visible = items.slice(0, MAX_VISIBLE);
  const images = visible.filter((it) => it.kind === 'image');
  const docs = visible.filter((it) => it.kind === 'pdf' || it.kind === 'doc');
  const imageLayout = layoutFor(images.length);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (visible.length === 0) return null;

  return (
    <>
      <Stack gap="sm">
        {images.length > 0 ? (
          imageLayout === '1' ? (
            <PostMediaSingleImage
              item={images[0]!}
              onOpen={() => setLightboxIndex(0)}
            />
          ) : (
            <div style={gridStyleFor(imageLayout)}>
              {images.map((item, idx) => (
                <PostMediaImageTile
                  key={item.id}
                  item={item}
                  style={tileSpanFor(imageLayout, idx)}
                  onOpen={() => setLightboxIndex(images.indexOf(item))}
                />
              ))}
            </div>
          )
        ) : null}
        {docs.length > 0 ? (
          <Stack gap="sm">
            {docs.map((item) => (
              <PostMediaDocumentCard key={item.id} item={item} />
            ))}
          </Stack>
        ) : null}
      </Stack>
      {lightboxIndex !== null ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setLightboxIndex(null);
          }}
          accessibleTitle={t('media.dialogTitle')}
          size="lg"
        >
          <PostMediaLightbox item={images[lightboxIndex]!} />
        </Dialog>
      ) : null}
    </>
  );
}

type TileProps = {
  item: PostMediaView;
  style?: CSSProperties;
  onOpen: () => void;
};

function PostMediaImageTile({ item, style, onOpen }: TileProps) {
  const t = useTranslations('Feed');
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={item.altText ?? t('media.openImage')}
      style={imageTileWrapperStyles({ cursor: 'zoom-in', ...style })}
    >
      <PostMediaImageBody item={item} />
    </button>
  );
}

function filenameFor(
  item: PostMediaView,
  t: ReturnType<typeof useTranslations<'Feed'>>,
): string {
  if (item.altText !== null && item.altText.length > 0) return item.altText;
  return item.kind === 'doc'
    ? t('media.documentDocx')
    : t('media.documentPdf');
}

function PostMediaLightbox({ item }: { item: PostMediaView }) {
  const t = useTranslations('Feed');
  if (item.kind === 'pdf') {
    return (
      <iframe
        title={item.altText ?? t('media.pdfTitle')}
        src={item.url}
        style={{ width: '100%', height: '70vh', border: 'none' }}
      />
    );
  }
  if (item.kind === 'doc') {
    // Browsers can't render DOCX natively; the document pill is the primary
    // open path (target=_blank → download). Reaching the lightbox for a doc
    // would be a routing bug, but we still render something rather than a
    // crashing branch.
    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer">
        {filenameFor(item, t)}
      </a>
    );
  }
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio:
          item.width !== undefined && item.height !== undefined
            ? `${item.width} / ${item.height}`
            : '4 / 3',
        maxHeight: '80vh',
      }}
    >
      <img
        src={item.url}
        alt={item.decorative ? '' : (item.altText ?? '')}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editable variant — composer-only. WYSIWYG with the read-only gallery:
// images render in the same grid using the same image body, docs render as
// stacked PostMediaDocumentCard.Editable cards. Layout + body match the
// rendered post; the only addition is per-tile editing chrome (remove,
// reorder for images via arrow keys, retry-on-error, alt-text editor for
// images).
// ---------------------------------------------------------------------------

export type EditableMediaItem = PostMediaView & {
  /** A blob: URL or final CDN URL. */
  uploading?: boolean;
  uploadPct?: number | null;
  uploadError?: string | null;
};

type EditableProps = {
  items: ReadonlyArray<EditableMediaItem>;
  onAlt: (id: string, value: string) => void;
  onDecorative: (id: string, value: boolean) => void;
  onRemove: (id: string) => void;
  onReorder: (fromId: string, toId: string) => void;
  onRetry?: (id: string) => void;
};

function PostMediaGalleryEditable({
  items,
  onAlt,
  onDecorative,
  onRemove,
  onReorder,
  onRetry,
}: EditableProps) {
  const visible = items.slice(0, MAX_VISIBLE);
  const images = visible.filter((it) => it.kind === 'image');
  const docs = visible.filter((it) => it.kind === 'pdf' || it.kind === 'doc');
  const imageLayout = layoutFor(images.length);

  // Reorder neighbours within the full visible-items array (mixed kinds),
  // not within the kind-split sub-arrays — the underlying media[] position
  // is single-list, so keeping the keyboard nav anchored to that ordering
  // matches submit semantics.
  return (
    <Stack gap="md">
      {images.length > 0 ? (
        imageLayout === '1' ? (
          <EditableSingleImage
            item={images[0]!}
            position={visible.indexOf(images[0]!)}
            total={visible.length}
            onRemove={() => onRemove(images[0]!.id)}
            {...(onRetry !== undefined
              ? { onRetry: () => onRetry(images[0]!.id) }
              : {})}
          />
        ) : (
          <div style={gridStyleFor(imageLayout)}>
            {images.map((item, idx) => {
              const indexInAll = visible.indexOf(item);
              const leftNeighbour =
                indexInAll > 0 ? visible[indexInAll - 1] : undefined;
              const rightNeighbour =
                indexInAll < visible.length - 1
                  ? visible[indexInAll + 1]
                  : undefined;
              return (
                <EditableImageTile
                  key={item.id}
                  item={item}
                  position={indexInAll}
                  total={visible.length}
                  style={tileSpanFor(imageLayout, idx)}
                  onRemove={() => onRemove(item.id)}
                  onMoveLeft={
                    leftNeighbour !== undefined
                      ? () => onReorder(item.id, leftNeighbour.id)
                      : undefined
                  }
                  onMoveRight={
                    rightNeighbour !== undefined
                      ? () => onReorder(item.id, rightNeighbour.id)
                      : undefined
                  }
                  {...(onRetry !== undefined
                    ? { onRetry: () => onRetry(item.id) }
                    : {})}
                />
              );
            })}
          </div>
        )
      ) : null}
      {docs.length > 0 ? (
        <Stack gap="sm">
          {docs.map((item) => (
            <PostMediaDocumentCard.Editable
              key={item.id}
              item={item}
              onRemove={() => onRemove(item.id)}
              {...(onRetry !== undefined
                ? { onRetry: () => onRetry(item.id) }
                : {})}
            />
          ))}
        </Stack>
      ) : null}
      {images.length > 0 ? (
        <Stack gap="sm">
          {images.map((item) => (
            <ItemAltField
              key={item.id}
              item={item}
              onAlt={(v) => onAlt(item.id, v)}
              onDecorative={(v) => onDecorative(item.id, v)}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

// Composer preview of a lone image — mirrors the read-only PostMediaSingleImage
// (uncropped, height-capped) so the composer stays WYSIWYG with the feed, plus
// the remove control and upload-state overlays. No reorder: there's only one.
function EditableSingleImage({
  item,
  position,
  total,
  onRemove,
  onRetry,
}: {
  item: EditableMediaItem;
  position: number;
  total: number;
  onRemove: () => void;
  onRetry?: () => void;
}) {
  const t = useTranslations('Feed');
  return (
    <div
      role="group"
      aria-label={t('media.attachedMediaAria', {
        position: position + 1,
        total,
      })}
      style={{ position: 'relative', width: '100%' }}
    >
      <img
        src={item.url}
        alt={item.decorative ? '' : (item.altText ?? '')}
        style={singleImageStyles()}
        {...(item.decorative ? { role: 'presentation' as const } : {})}
      />
      {item.uploading === true ? (
        <TileOverlay scrim="dim" ariaLive="polite">
          <Label style={{ color: 'white' }}>
            {item.uploadPct !== null && item.uploadPct !== undefined
              ? t('media.uploadingPct', { pct: item.uploadPct })
              : t('media.uploading')}
          </Label>
        </TileOverlay>
      ) : null}
      {item.uploadError !== null && item.uploadError !== undefined ? (
        <TileOverlay scrim="dark" stack>
          <Body size="sm" style={{ color: 'white' }}>
            {item.uploadError}
          </Body>
          {onRetry !== undefined ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              {t('media.retry')}
            </Button>
          ) : null}
        </TileOverlay>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('media.removeFile')}
        style={{
          position: 'absolute',
          top: 'var(--space-xs)',
          insetInlineEnd: 'var(--space-xs)',
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <Icon size={14}>{I.x}</Icon>
      </button>
    </div>
  );
}

function EditableImageTile({
  item,
  position,
  total,
  style,
  onRemove,
  onMoveLeft,
  onMoveRight,
  onRetry,
}: {
  item: EditableMediaItem;
  position: number;
  total: number;
  style?: CSSProperties;
  onRemove: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  onRetry?: () => void;
}) {
  const t = useTranslations('Feed');
  return (
    <div
      role="group"
      aria-label={t('media.attachedMediaAria', {
        position: position + 1,
        total,
      })}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' && onMoveLeft !== undefined) {
          e.preventDefault();
          onMoveLeft();
        } else if (e.key === 'ArrowRight' && onMoveRight !== undefined) {
          e.preventDefault();
          onMoveRight();
        }
      }}
      tabIndex={0}
      style={imageTileWrapperStyles({ outline: 'none', ...style })}
    >
      <PostMediaImageBody item={item} />
      {item.uploading === true ? (
        <TileOverlay scrim="dim" ariaLive="polite">
          <Label style={{ color: 'white' }}>
            {item.uploadPct !== null && item.uploadPct !== undefined
              ? t('media.uploadingPct', { pct: item.uploadPct })
              : t('media.uploading')}
          </Label>
        </TileOverlay>
      ) : null}
      {item.uploadError !== null && item.uploadError !== undefined ? (
        <TileOverlay scrim="dark" stack>
          <Body size="sm" style={{ color: 'white' }}>
            {item.uploadError}
          </Body>
          {onRetry !== undefined ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              {t('media.retry')}
            </Button>
          ) : null}
        </TileOverlay>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('media.removeFile')}
        style={{
          position: 'absolute',
          top: 'var(--space-xs)',
          insetInlineEnd: 'var(--space-xs)',
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <Icon size={14}>{I.x}</Icon>
      </button>
    </div>
  );
}

function ItemAltField({
  item,
  onAlt,
  onDecorative,
}: {
  item: EditableMediaItem;
  onAlt: (value: string) => void;
  onDecorative: (value: boolean) => void;
}) {
  const t = useTranslations('Feed');
  return (
    <Stack gap="xs">
      <Row gap="sm" align="center">
        <input
          type="text"
          placeholder={t('media.describeImage')}
          value={item.altText ?? ''}
          onChange={(e) => onAlt(e.target.value)}
          disabled={item.decorative}
          maxLength={1000}
          style={{
            flex: 1,
            font: 'var(--type-caption)',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- native <input> alt-text field; no Box prop
            padding: 'var(--space-xs) var(--space-sm)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            background: item.decorative ? 'var(--muted)' : 'var(--background)',
            color: 'var(--foreground)',
          }}
          aria-label={t('media.describeImage')}
        />
        <Checkbox
          checked={item.decorative}
          onCheckedChange={(next) => onDecorative(next === true)}
          label={
            <Label size="sm">{t('media.decorative')}</Label>
          }
        />
      </Row>
    </Stack>
  );
}

PostMediaGallery.Editable = PostMediaGalleryEditable;

// Centered overlay for tile-state messages (uploading / failed). Single
// source of truth for the dim-or-dark scrim + centered content pattern so
// callers never reach for inline `position: absolute` + scrim values.
function TileOverlay({
  children,
  scrim,
  stack = false,
  ariaLive,
}: {
  children: ReactNode;
  scrim: 'dim' | 'dark';
  stack?: boolean;
  ariaLive?: 'polite' | 'assertive';
}) {
  const props = ariaLive !== undefined ? { 'aria-live': ariaLive } : {};
  return (
    <div
      {...props}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: stack ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: stack ? 'var(--space-sm)' : undefined,
        padding: stack ? 'var(--space-md)' : undefined,
        textAlign: 'center',
        background: scrim === 'dim' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.6)',
      }}
    >
      {children}
    </div>
  );
}
